/**
 * 백엔드의 거절을 **사람이 할 일로 가른다**(명세 2-5절).
 *
 * ⚠ **전부 403 화면 하나로 뭉개지 않는다**(레거시 원칙 그대로). 갈래마다 사람이
 * 해야 할 일이 서로 다르다 —
 *
 *   세션 만료     다시 로그인해야 한다
 *   PIN 필요      로그아웃하면 안 된다. 키패드만 다시 받으면 하던 일을 잇는다
 *   처음 설정     임시 비밀번호로 들어온 세션이다. 비밀번호·PIN 을 정해야 한다
 *   권한 없음     이 사람은 여기 못 온다. 재시도 버튼을 주면 안 된다
 *   계정 비활성   권한 문제가 아니다. 관리자에게 계정을 열어 달라고 해야 한다
 *
 * 새 계약(10_API계약.md 0-3)의 오류 본문은 `{ "error": "<code>", "message": "<한국어>" }`
 * 하나다. 레거시는 `code` 칸을 봤다 — 이 웹은 `error` 칸으로 가른다(명세 0-3 고침).
 *
 * 새 계약은 「방금 넣은 자격이 틀렸다」를 **400** 으로 준다(`wrong_password`·`wrong_pin`).
 * 그래서 레거시의 「401 인데 로그아웃하지 않는 code」 목록은 필요 없다. (직원 OTP 의 `wrong_otp`·`otp_locked` 는
 * 정할 것 39 로 OTP 를 없애 두지 않는다.)
 */

/** 계약 0-3 의 오류 본문. `reasonCode` 는 같은 `error` 안의 경우를 가르는 종류(있을 때만 — 2026-10-01 새 칸). */
import { serverErrorText } from '../i18n/serverErrors.en'

export interface ApiErrorBody {
  error: string
  message: string
  reasonCode?: string
  /** 2026-10-21: 그 경우의 정확한 코드(`error` 또는 `error.<경우>` — 거래소 서버 `error_codes` 표의 한 줄). */
  code?: string
  /** 2026-10-21: 그 코드 글의 `{0}` `{1}` 에 들어갈 값. */
  args?: string[]
}

export type ApiFailureKind =
  | 'session_expired'
  | 'pin_required'
  | 'setup_required'
  | 'permission_denied'
  | 'account_inactive'
  | 'bad_request'
  | 'not_found'
  | 'halted'
  | 'server'
  | 'network'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly kind: ApiFailureKind
  /** 서버가 준 한국어 문구(계약 0-3). 없으면 null. */
  readonly serverMessage: string | null
  /** 같은 `error` 안의 경우(계약 0-3 `reasonCode` — 예 `super_admin_only`·빈 칸 이름). 없으면 null. */
  readonly reasonCode: string | null
  /** 정확한 코드(2026-10-21 새 칸 — 언어 파일 `i18n/serverErrors.en.ts` 의 열쇠). 없으면 null. */
  readonly exactCode: string | null
  /** 그 코드 글의 값들. */
  readonly args: readonly string[]

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.message ?? 'The request could not be processed.')
    this.name = 'ApiError'
    this.status = status
    this.code = body.error ?? 'unknown'
    this.kind = classify(status, this.code)
    this.serverMessage = typeof body.message === 'string' && body.message.trim() !== ''
      ? body.message
      : null
    this.reasonCode = typeof body.reasonCode === 'string' && body.reasonCode !== '' ? body.reasonCode : null
    this.exactCode = typeof body.code === 'string' && body.code !== '' ? body.code : null
    this.args = Array.isArray(body.args) ? body.args.map(String) : []
  }
}

/**
 * 상태코드와 code 를 사람이 할 일로 옮긴다 — `code` 를 먼저 보고, 그다음 상태코드를 본다.
 *
 * ⚠ **403 이 여러 뜻이다.** `pin_required`·`setup_required`·`not_an_admin`·`forbidden`·
 * `self_approval`·`ip_not_allowed` 가 모두 403 이다. `self_approval` 은 권한 없음이 아니다 — 창 안에서
 * 「자기 요청은 자기가 승인할 수 없다」고 말해야 한다(명세 2-5). `ip_not_allowed`(정할 것 39 — 관리자 접속 아이피
 * 허용 목록 밖 주소, 계약 0-2)도 권한(키) 문제가 아니다 — 「권한이 없습니다」로 덮지 않고 그 글을 보인다.
 */
function classify(status: number, code: string): ApiFailureKind {
  if (code === 'network') return 'network'
  if (code === 'pin_required') return 'pin_required'
  if (code === 'setup_required') return 'setup_required'
  if (code === 'not_an_admin') return 'permission_denied'
  if (code === 'self_approval') return 'bad_request'
  if (code === 'ip_not_allowed') return 'bad_request'
  if (code === 'exchange_halted') return 'halted'

  switch (status) {
    case 401:
      return 'session_expired'
    case 403:
      return 'permission_denied'
    case 400:
      return 'bad_request'
    case 404:
      return 'not_found'
    default:
      return status >= 500 ? 'server' : 'bad_request'
  }
}

/**
 * 갈래마다 한 문구. 레거시 `FAILURE_MESSAGE` 여덟 줄 그대로(명세 4-4 '갈래 공통 8개')에
 * 새 갈래 둘을 더했다 — 처음 설정(EA-39)·거래소 점검(4-4 `exchange_halted`).
 */
export const FAILURE_MESSAGE: Record<ApiFailureKind, string> = {
  session_expired: 'Your sign-in has expired. Please sign in again.',
  pin_required: 'Please enter your PIN.',
  setup_required: 'Finish the first setup to see this screen.',
  permission_denied: 'You do not have permission to access this feature.',
  account_inactive: 'This admin account is not active.',
  bad_request: 'The request is not valid.',
  not_found: 'Not found.',
  halted: 'The exchange is under maintenance. Please try again later.',
  server: 'A server error occurred. Please try again later.',
  network: 'Could not connect to the server.',
}

/**
 * code 별 문구 (명세 4-4). 표에 없는 code 는 서버 `message` 를 보인다.
 *
 * ⚠ 같은 code 가 자리마다 다른 문구를 갖는 것(`wrong_password`·`wrong_pin`·`forbidden`·
 * `unauthorized`)은 여기 두지 않고 [errorMessage] 의 자리(`MessageContext`)로 가른다.
 */
export const ERROR_MESSAGE: Record<string, string> = {
  account_locked: 'The account is locked. Please contact an administrator.',
  too_many_requests: 'Too many attempts. Please try again later.',
  invalid_pin: 'The PIN is 6 digits.',
  pin_locked: 'The PIN was wrong too many times. Please try again in 10 minutes.',
  pin_not_set: 'No PIN is set. Please complete the first setup.',
  same_pin: 'The new PIN must be different from the current PIN.',
  invalid_password: 'The password must be 8–64 characters and include letters, numbers and symbols.',
  already_reviewed: 'This application has already been processed.',
  self_approval: 'You cannot approve a request you made yourself.',
  undetermined_values: 'Some values are not decided yet, so this cannot be approved.',
  deduction_in_flight: 'The fee deduction that was sent is not confirmed on the chain yet. Please try again later.',
  collateral_judgement_pending: 'The collateral is waiting to be judged again. Please try again later.',
  capacity_deposit_in_flight: 'A change of the per-seat collateral is in progress.',
  settlement_shortfall: 'The collateral balance does not cover the penalty and the fees, so this cannot be approved.',
  // 사장님 2026-10-15 「철회신청이되있는데 굳이 관리자가 철회 신청중인걸 알면 박탈할필요가없잖아 / 차라리 철회 승인이 되는게 맞지」 —
  // 박탈 요청·승인(XM-38·XM-41)의 409: 철회 신청이 대기 중인 마스터는 철회 승인(XM-49)으로.
  withdrawal_request_pending: 'This master has a pending withdrawal request. Please process it by approving the withdrawal.',
  collateral_contract_not_ready: 'The collateral contract is not confirmed yet.',
  collateral_code_id_not_set: 'The collateral contract is not configured, so this cannot be approved now.',
  wallet_address_required: 'This member has no registered wallet address.',
  login_id_taken: 'This ID is already in use.',
  invalid_email: 'The email address is not in a valid format.',
  mail_not_configured: 'Mail cannot be sent, so nothing was changed.',
  mail_unavailable: 'Mail cannot be sent, so nothing was changed.',
  name_taken: 'A permission name with this name already exists.',
  unknown_key: 'It contains an unknown page or button.',
  name_in_use: 'Staff members have this permission name, so it cannot be deleted.',
  capacity_full: "That master's capacity is full.",
  capacity_exceeded: 'The number of members under the master exceeds the new capacity, so this cannot be approved.',
  insufficient_collateral: 'The collateral balance does not cover 30% of the new per-seat collateral and the remaining fees, so this cannot be approved. Please top up first.',
  exchange_halted: 'The exchange is under maintenance. Please try again later.',
  not_connected: 'Not connected to the trade ledger.',
  // 정할 것 39(2026-10-13) — 서버 글과 같다(계약 0-3).
  ip_not_allowed: 'This address is not allowed to use the admin web.',
}

/**
 * `reasonCode` 별 문구 — 같은 code(예 403 `forbidden`)를 갈래 문구(「권한이 없습니다」)로 덮지 않을 때.
 * `super_admin_only`: 서버 점검 켜기·끄기(XM-105)를 최고관리자가 아닌 관리자가 보냄 — 이 웹이 가진 글(서버 `message` 가 아님 —
 * 2026-10-02 부터 서버 글도 같은 글, 사장님 「최고관리자(admin) 한 사람만 서버점검 누를 수 있다.」, 거래소백엔드 5758ccc). 앞 판의 `general_admin_only`
 * (2026-10-01 「총괄 관리자만 승인가능」)는 서버가 더 보내지 않아 두지 않는다.
 */
export const REASON_MESSAGE: Record<string, string> = {
  super_admin_only: 'Only the super admin can turn server maintenance on or off.',
}

/** 「지금은 체인에 보낼 수 없습니다. ({code})」 로 말하는 code (명세 4-4). */
const CHAIN_NOT_READY: ReadonlySet<string> = new Set([
  'chain_not_configured',
  'chain_broadcast_disabled',
  'admin_key_unavailable',
  'contract_not_configured',
])

/**
 * 같은 code 가 다른 문구를 갖는 자리 (명세 4-4).
 *
 * - `login`: 로그인(EA-01) — `wrong_password` 는 아이디·비밀번호, `forbidden` 은 쓸 수 없는 계정
 * - `pin_gate`: PIN 관문(EA-04) — `wrong_pin` 은 PIN 틀림
 * - `pin_change`: PIN 변경(EA-38) — `wrong_pin` 은 현재 PIN 틀림
 * - `password_change`: 비밀번호 변경(EA-38) — `wrong_password` 는 현재 비밀번호 틀림. 서버 글이 먼저다(아래 [SERVER_TEXT_FIRST])
 * - `incident_open`: 사고 문 열기(EA-25) — 틀린 시도도 감사 기록에 남는다. 서버 글이 먼저다
 * - `material_mail`: 자료 메일 보내기(XM-114) — 메일 서버가 받지 않으면 `FAILED` 기록이 남는다
 *   (직원 임시 비밀번호의 「아무것도 바꾸지 않았습니다」와 다르다), 메일 설정이 없으면 기록도 없다
 */
export type MessageContext =
  | 'login'
  | 'pin_gate'
  | 'pin_change'
  | 'password_change'
  | 'incident_open'
  | 'material_mail'

const CONTEXT_MESSAGE: Record<MessageContext, Record<string, string>> = {
  login: {
    wrong_password: 'The ID or password is not correct.',
    forbidden: 'This account cannot be used.',
  },
  pin_gate: {
    wrong_pin: 'The PIN is not correct.',
  },
  pin_change: {
    wrong_pin: 'The current PIN is not correct.',
  },
  password_change: {
    wrong_password: 'The current password is not correct.',
    password_check_blocked: 'The password was wrong 5 times. Please try again in 5 minutes.',
    account_locked: 'The password was wrong 10 times in a row, so this account is locked and signed out. Please contact the super admin.',
  },
  incident_open: {
    wrong_password: 'The password is not correct. This attempt was also recorded in the audit log.',
    password_check_blocked: 'The password was wrong 5 times. Please try again in 5 minutes.',
    account_locked: 'The password was wrong 10 times in a row, so this account is locked and signed out. Please contact the super admin.',
  },
  material_mail: {
    mail_unavailable: 'The mail server did not accept the mail, so it was not sent. A failure record was kept (it shows when you open the details again).',
    mail_not_configured: 'Mail is not configured, so nothing was sent. No record was kept.',
  },
}

/**
 * 서버 글이 먼저인 자리·code — 정할 것 21·34·37(사장님 21번 표 「3회 실패 — 경고 메시지 노출(예: "2회 더 실패 시 5분간
 * 차단됩니다.")」 / 「5회 실패 — 1단계 임시 잠금」 / 「10회 연속 실패 — 최종 잠금」). 내 비밀번호 바꾸기(XM-08)·사고 두 번째 문
 * (XM-58)의 틀린 비밀번호는 서버가 횟수마다 다른 글을 준다(1·2·6~9번째 'The password is not correct.', 3·4번째 경고, 5번째
 * 막힘, 10번 연속 최종 잠금 — 계약 2026-10-13). 이 웹은 몇 번째인지 모르므로 서버 글을 그대로 보이고, 서버 글이 없을 때만
 * 위 [CONTEXT_MESSAGE] 의 글을 쓴다. 사고 문의 틀린 비밀번호에는 기록 안내를 붙인다.
 */
const SERVER_TEXT_FIRST: Partial<Record<MessageContext, ReadonlySet<string>>> = {
  password_change: new Set(['wrong_password', 'password_check_blocked', 'account_locked']),
  incident_open: new Set(['wrong_password', 'password_check_blocked', 'account_locked']),
}

/** 사고 문의 틀린 비밀번호 뒤에 붙는 안내(서버가 `INCIDENT_DETAIL_DENIED` 를 남긴다). */
const AUDIT_NOTE = 'This attempt was also recorded in the audit log.'

/** 앱 차원의 갈래 — 자리와 상관없이 늘 같은 문구다. */
const FIXED_KINDS: ReadonlySet<ApiFailureKind> = new Set<ApiFailureKind>([
  'session_expired', 'pin_required', 'setup_required', 'permission_denied', 'network',
])

/**
 * 화면이 띄울 한 문장.
 *
 * 순서: 서버 글이 먼저인 자리(비밀번호 다시 확인 — 정할 것 21·34·37) → 자리별 문구 → `reasonCode` 문구 →
 * 앱 차원 갈래(세션·PIN·처음 설정·권한·연결) → code 문구(4-4) → 체인 준비 안 됨 → 없음 → 정확한 코드의 언어 파일 글
 * (`i18n/serverErrors.en.ts`, 2026-10-21) → 영어 서버 `message` → 갈래 문구.
 *
 * ⚠ **모르는 code 의 영어 안내는 보존한다.** 한국어가 남은 서버 문구는 상태에 맞는
 * 영어 안내로 바꾼다(관01 14-5). 문맥별 서버 글 우선순위는 앞에서 처리한다.
 */
export function errorMessage(err: unknown, ctx?: MessageContext): string {
  if (!(err instanceof ApiError)) return FAILURE_MESSAGE.server
  if (ctx && err.serverMessage && SERVER_TEXT_FIRST[ctx]?.has(err.code)) {
    return ctx === 'incident_open' && err.code === 'wrong_password' ? `${err.serverMessage} ${AUDIT_NOTE}` : err.serverMessage
  }
  const byContext = ctx ? CONTEXT_MESSAGE[ctx][err.code] : undefined
  if (byContext) return byContext
  const byReason = err.reasonCode ? REASON_MESSAGE[err.reasonCode] : undefined
  if (byReason) return byReason
  if (FIXED_KINDS.has(err.kind)) return FAILURE_MESSAGE[err.kind]
  const byCode = ERROR_MESSAGE[err.code]
  if (byCode) return byCode
  if (CHAIN_NOT_READY.has(err.code)) return `This cannot be sent to the chain right now. (${err.code})`
  if (err.kind === 'not_found') return FAILURE_MESSAGE.not_found
  // 2026-10-21 (사장님 「코드에따라서 lang파일에서 메시지 표시」): 정확한 코드의 글은 이 웹의 언어 파일에서
  const byExact = serverErrorText(err.exactCode, err.args)
  if (byExact) return byExact
  const message = err.serverMessage
  return message && /\p{Script=Hangul}/u.test(message)
    ? FAILURE_MESSAGE[err.kind]
    : message ?? FAILURE_MESSAGE[err.kind]
}
